import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ToastVariants } from '@dotcom/crown-ui';
import { useAnalyticsContext } from '../../../../context/analytics-context';
import {
  setAllDrawersClosed,
  setPhotoDrawerClosed,
  setIsClearConfirmationDialogClosed,
  setIsDeleteConfirmationDialogClosed,
  setIsDeleteConfirmationDialogOpen,
  setIsImageEditDrawerOpen,
  setIsResetConfirmationDialogClosed,
  setIsToasterOpen,
  useAppContext,
} from '../../../../context/app-context';
import { useUIStore } from '../../../../stores/ui';
import { useCardContext } from '../../../../context/card-context';
import { useCropContext } from '../../../../context/crop-context';
import { useInitializationDataContext } from '../../../../context/data-context';
import { usePhotoControls } from '../../../../features/card-editor/hooks/use-photo-controls';
import { addEditableAreas } from '../../../card-editor/utils';
import {
  enqueueCanvasOperation,
  useHistoryTransaction,
  useHistoryTransactionErrorToast,
} from '../../../../features/edit-history/edit-history';
import { isFabricImage } from '../../../../features/card-editor/utils/is-fabric-image';
import { isPhotozoneImage } from '../../../../features/card-editor/utils/is-photozone-image';
import { FabricObject, FabricTextBox } from '../../../../global-types';
import { ActionType, useDatadog } from '../../../../hooks/use-datadog';
import { useIsOneToMany } from '../../../../hooks/use-is-one-to-many';
import { useSaveProject } from '../../../../hooks';
import { deleteImage } from '../../../../services';
import {
  CanvasDataTypes,
  clearPhotoTextZone,
  convertPointToPixel,
  getObjectByName,
  togglePhotoTextZoneButtons,
} from '../../../../utils';
import { parseTextAction } from '../../../../utils/parse-text-action';
import { handleOrderChange } from '../utils/handle-order-change';
import { isUserZoneEmpty } from '../utils/is-user-zone-empty';

export const useDrawerContainerActions = (
  canvas: React.RefObject<fabric.Canvas> | undefined,
  setInitialAngle: React.Dispatch<React.SetStateAction<number>>,
) => {
  const { addAction, textAction } = useDatadog();
  const { appDispatch } = useAppContext();
  const { finishCropping } = useCropContext();
  const openTextDrawer = useUIStore((state) => state.openTextDrawer);
  const closeTextDrawer = useUIStore((state) => state.closeTextDrawer);
  const { updateEditFormats, trackEditElement, trackDeleteElement, isReplacingImage, trackWamTamReset } =
    useAnalyticsContext();
  const { cardState } = useCardContext();
  const isOneToMany = useIsOneToMany();
  const { t } = useTranslation();
  const { deleteSelectedPhoto, changePhotoOrder } = usePhotoControls();
  const runHistoryTransaction = useHistoryTransaction();
  const showHistoryTransactionError = useHistoryTransactionErrorToast();
  const { saveProject } = useSaveProject();

  const openImageEditDrawer = useCallback(() => setIsImageEditDrawerOpen(appDispatch), [appDispatch]);

  const {
    initializedDataState: { data: initializedData },
  } = useInitializationDataContext();

  const resetErrorMessage = t('drawerContainer.resetErrorMessage');
  const imageErrorTitle = t('drawerContainer.imageErrorTitle');
  const handleRotateOpenDrawer = () => {
    const activeObject = canvas?.current?.getActiveObject();
    if (activeObject?.type === 'image') {
      openImageEditDrawer();
    } else {
      openTextDrawer();
    }
  };

  // TO DO Check if we use this function in other fabric objects besides images
  const onChangeOrder = async (order: number, object?: fabric.Object) => {
    if (isFabricImage(object)) {
      changePhotoOrder(order, object);
      return;
    }
    const targetCanvas = canvas?.current;
    if (!targetCanvas) return;
    const targetObject = object ?? targetCanvas.getActiveObject();
    if (!targetObject || targetCanvas.getObjects().indexOf(targetObject) === order) return;
    try {
      await runHistoryTransaction(targetCanvas, () => handleOrderChange(order, targetCanvas, targetObject));
      updateEditFormats({ order: order.toString() });
    } catch {
      showHistoryTransactionError();
    }
  };

  const openDeleteConfirmationDialog = () => {
    setIsDeleteConfirmationDialogOpen(appDispatch);
  };

  const closeDeleteConfirmationDialog = () => {
    setIsDeleteConfirmationDialogClosed(appDispatch);
  };

  const closeClearConfirmationDialog = () => {
    trackDeleteElement();
    setIsClearConfirmationDialogClosed(appDispatch);
    resetSelection();
  };

  const onSubmitSize = () => {
    openTextDrawer();
  };

  const onSubmitRotate = () => {
    const activeObject = canvas?.current?.getActiveObject();
    setInitialAngle(activeObject?.angle ?? 0);

    if (activeObject && isPhotozoneImage(activeObject)) {
      // Reset scaleValue to 100 when applying rotation for photozone images since the scale is recalculated
      // to ensure the image still covers the photozone after rotation
      activeObject?.set({
        data: { ...activeObject.data, scaleValue: 100 },
      });
    }

    handleRotateOpenDrawer();
  };

  const onSubmitOrder = () => {
    const activeObject = canvas?.current?.getActiveObject();

    if (activeObject?.type === 'image') {
      setIsImageEditDrawerOpen(appDispatch);
    } else {
      openTextDrawer();
    }
  };

  const onFinishCropping = async () => {
    if (!finishCropping) return;

    try {
      await finishCropping(runHistoryTransaction);
      saveProject({ showLoadingScreen: false, shouldRestoreCanvas: false, generateFrontPreview: false });
    } catch {
      showHistoryTransactionError();
    }
  };

  const onCloseImageEditDrawer = () => {
    if (!isReplacingImage.current) {
      trackEditElement();
      resetSelection();
    }
  };

  /**
   *  When size drawer is closed by cancel, revert text to its original size
   *
   * @param textObject - object to revert back to its original size
   * @param fontSize - the text object's original size

   * @returns void
   */
  const onSizeDrawerClose = (textObject: fabric.Textbox, fontSize: number) => {
    openTextDrawer();
    textObject?.setOptions({ fontSize });
    canvas?.current?.renderAll();
  };

  const closeResetConfirmationDialog = () => {
    setIsResetConfirmationDialogClosed(appDispatch);
  };

  const resetSelection = () => {
    setAllDrawersClosed(appDispatch);
    closeTextDrawer();
    if (canvas?.current) {
      canvas.current.discardActiveObject();
      canvas.current.renderAll();
    }
  };

  const deleteSelectedObject = () => {
    const activeObject = canvas?.current?.getActiveObject();
    if (activeObject) {
      trackDeleteElement();
      canvas?.current?.remove(activeObject);
    }

    if (
      activeObject?.type === CanvasDataTypes.EditableText ||
      activeObject?.type === CanvasDataTypes.UserText ||
      activeObject?.type === CanvasDataTypes.PhotoZoneTextbox
    ) {
      textAction(ActionType.DELETE_TEXT, parseTextAction(activeObject as FabricTextBox));
    }
    return activeObject;
  };

  const handleClearConfirmation = async () => {
    const targetCanvas = canvas?.current;
    if (!targetCanvas) return;

    try {
      await runHistoryTransaction(targetCanvas, () => clearPhotoTextZone(targetCanvas));
      setAllDrawersClosed(appDispatch);
      setPhotoDrawerClosed(appDispatch);
      closeTextDrawer();
      closeClearConfirmationDialog();
    } catch {
      showHistoryTransactionError();
    }
  };

  const handleResetConfirmation = () => {
    const activeImage = canvas?.current?.getActiveObject() as fabric.Object;
    if (!activeImage) {
      return;
    }

    if (activeImage.data?.type === CanvasDataTypes.WamImage) {
      addAction('remove-handwriting-from-card-face', {
        handwriting: activeImage.data,
      });
    }

    deleteImage(initializedData?.project_id as string, activeImage?.name as string).then((response) => {
      if (response?.meta?.code >= 400) {
        setIsToasterOpen(appDispatch, {
          title: imageErrorTitle,
          children: resetErrorMessage,
          variant: ToastVariants.Error,
        });
        closeResetConfirmationDialog();
      } else {
        const deletedImage = deleteSelectedObject();
        if (deletedImage && deletedImage.data?.zoneName && canvas?.current) {
          const photoTextZone = getObjectByName(deletedImage.data.zoneName, canvas.current) as fabric.Group;
          if (photoTextZone) {
            photoTextZone.data.hasContent = false;
            photoTextZone.setOptions({ selectable: true });
            togglePhotoTextZoneButtons(true, canvas.current, photoTextZone);
            canvas?.current?.renderAll();
          }
        }
        setAllDrawersClosed(appDispatch);
        setPhotoDrawerClosed(appDispatch);
        closeTextDrawer();
        closeResetConfirmationDialog();

        if (deletedImage?.data?.zoneName?.indexOf(CanvasDataTypes.PhotoTextZone) !== -1) {
          //Track Wam/Tam reset
          trackWamTamReset(false);
        }

        saveProject({ showLoadingScreen: false, shouldRestoreCanvas: false, generateFrontPreview: false });
      }
    });
  };

  const [isDeleting, setIsDeleting] = useState(false);

  const handleDeleteConfirmation = async () => {
    const activeObject = canvas?.current?.getActiveObject() as FabricObject | null;

    if (!canvas || !canvas.current || !activeObject) return;

    setIsDeleting(true);
    try {
      const activeCanvas = canvas.current;
      const restoreEmptyZoneHelpers = async () => {
        const objects = activeCanvas.getObjects();
        if (isUserZoneEmpty(objects)) {
          const cardFace = cardState.cardFacesList[`${cardState.activeCardIndex}`];
          const { editableAreas } = cardFace;
          if (editableAreas) {
            await enqueueCanvasOperation(activeCanvas, () => addEditableAreas(cardFace, cardState, true, isOneToMany));
          }
        }
      };
      if (isFabricImage(activeObject)) {
        await deleteSelectedPhoto(restoreEmptyZoneHelpers);
      } else {
        await runHistoryTransaction(activeCanvas, async () => {
          deleteSelectedObject();
          await restoreEmptyZoneHelpers();
        });
      }
      setAllDrawersClosed(appDispatch);
      setPhotoDrawerClosed(appDispatch);
      closeTextDrawer();
      closeDeleteConfirmationDialog();
      saveProject({ showLoadingScreen: false, shouldRestoreCanvas: false, generateFrontPreview: false });
    } catch {
      showHistoryTransactionError();
    } finally {
      setIsDeleting(false);
    }
  };

  const handleSizeChange = async (textObject: fabric.Textbox | null, valueInRange: string) => {
    const targetCanvas = canvas?.current;
    if (!textObject || !targetCanvas) return;

    const fontSize = Number.parseInt(valueInRange, 10);
    if (Number.isNaN(fontSize)) return;
    const scaledFontSize = convertPointToPixel(fontSize);
    try {
      await runHistoryTransaction(targetCanvas, () => {
        textObject.setOptions({ fontSize: scaledFontSize });
        textObject.fire('changed');
        targetCanvas.renderAll();
      });
      updateEditFormats({ fontSize: valueInRange });
    } catch {
      showHistoryTransactionError();
    }
  };

  return {
    handleRotateOpenDrawer,
    onChangeOrder,
    openDeleteConfirmationDialog,
    closeDeleteConfirmationDialog,
    closeClearConfirmationDialog,
    onSubmitSize,
    onSubmitRotate,
    onSubmitOrder,
    onFinishCropping,
    onCloseImageEditDrawer,
    onSizeDrawerClose,
    closeResetConfirmationDialog,
    handleClearConfirmation,
    deleteSelectedObject,
    handleResetConfirmation,
    handleDeleteConfirmation,
    handleSizeChange,
    isDeleting,
  };
};
